import type {
  AuditEvent,
  OverridePayload,
} from "../../../models/audit/auditEvent";

const hasText = (value: string | null | undefined): value is string =>
  !!value && value.trim().length > 0;

const pluralise = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** Long values are summarised rather than pasted — the redline has the full text. */
const SHORT_VALUE_LIMIT = 24;

/**
 * Change targets that name a bare session field rather than a thing. The
 * title already says what happened ("Renamed Test"), so repeating "name"
 * underneath is noise — the before/after pair lives in the expanded detail.
 */
const UNINFORMATIVE_CHANGE_TARGETS = new Set([
  "name",
  "template",
  "status",
  "test_phase",
  "fy_cycle_id",
]);

/**
 * Renders a before/after pair inline when both sides are short enough to read
 * at a glance ("fail → pass"), otherwise names the field alone. Returns
 * undefined when neither side carries text.
 */
function summariseChange(
  label: string,
  before: string | null | undefined,
  after: string | null | undefined,
): string | undefined {
  const from = before?.trim() ?? "";
  const to = after?.trim() ?? "";
  if (!from && !to) {
    return undefined;
  }
  const bothShort =
    from.length <= SHORT_VALUE_LIMIT && to.length <= SHORT_VALUE_LIMIT;
  if (!bothShort) {
    return label;
  }
  if (!from) {
    return `${label} → ${to}`;
  }
  if (!to) {
    return `${label} ${from} → empty`;
  }
  return `${label} ${from} → ${to}`;
}

/**
 * The most telling change on an override: prefer the per-field list, falling
 * back to the outcome/conclusion pair for events written before it existed.
 * Only the first is shown — the row is one line, the pane has the rest.
 */
function summariseOverride(override: OverridePayload): string | undefined {
  const [firstChange] = override.fieldChanges ?? [];
  if (firstChange) {
    const extra = (override.fieldChanges?.length ?? 0) - 1;
    const summary = summariseChange(
      firstChange.field,
      firstChange.before,
      firstChange.after,
    );
    if (summary) {
      return extra > 0 ? `${summary} +${extra} more` : summary;
    }
  }
  return summariseChange(
    "Outcome",
    override.outcomeBefore,
    override.outcomeAfter,
  );
}

/**
 * The one-line summary shown under an event's title. Leads with what actually
 * changed — "Outcome fail → pass", "3 samples", a document name — then the cell
 * it happened in, so a row is readable without opening the detail pane. Parts
 * already stated in the title are dropped so a row never reads
 * "Reran Sample: Sample 3 · Sample 3".
 *
 * Returns undefined when the event has no context worth a second line.
 */
export function buildEventContext(
  event: AuditEvent,
  columnNameById?: (columnId: string) => string | undefined,
): string | undefined {
  const parts: string[] = [];

  const runSampleCount = event.run?.sampleCount;
  const sampleCount = event.sample?.sampleCount;
  if (typeof runSampleCount === "number") {
    parts.push(`Ran ${pluralise(runSampleCount, "sample")}`);
  } else if (typeof sampleCount === "number") {
    parts.push(pluralise(sampleCount, "sample"));
  }

  if (event.override) {
    const overrideSummary = summariseOverride(event.override);
    if (overrideSummary) {
      parts.push(overrideSummary);
    }
  }

  const changeTarget = event.change?.target;
  if (event.change && hasText(changeTarget)) {
    const informative =
      !UNINFORMATIVE_CHANGE_TARGETS.has(changeTarget.toLowerCase()) &&
      !event.title.includes(changeTarget);
    const changeSummary = summariseChange(
      informative ? changeTarget : "",
      event.change.before,
      event.change.after,
    );
    if (changeSummary) {
      parts.push(changeSummary.trim());
    } else if (informative) {
      parts.push(changeTarget);
    }
  }

  if (event.sample && hasText(event.sample.fieldName)) {
    const sampleSummary = summariseChange(
      event.sample.fieldName,
      event.sample.before,
      event.sample.after,
    );
    parts.push(sampleSummary ?? event.sample.fieldName);
  }

  const documentNames = event.document?.documentNames;
  if (documentNames && documentNames.length > 0) {
    parts.push(
      documentNames.length > 2
        ? `${documentNames[0]} +${documentNames.length - 1} more`
        : documentNames.join(", "),
    );
  }

  if (hasText(event.citation?.summary)) {
    parts.push(event.citation.summary);
  }

  // Cell scope goes last — it answers "where", after "what". Events written
  // before the backend stamped `attributeLabel` fall back to resolving the
  // run's target columns client-side.
  const targetColumnIds = event.run?.targetColumnIds;
  const resolvedColumns =
    !hasText(event.attributeLabel) && targetColumnIds && columnNameById
      ? targetColumnIds.map((id) => columnNameById(id)).filter(hasText)
      : [];
  const columnScope =
    resolvedColumns.length > 2
      ? `${resolvedColumns[0]} +${resolvedColumns.length - 1} more`
      : resolvedColumns.join(", ");
  const scope = [
    hasText(event.attributeLabel) ? event.attributeLabel : columnScope,
    event.sampleLabel,
  ]
    .filter(hasText)
    .filter((part) => !event.title.includes(part))
    .join(" · ");
  if (scope) {
    parts.push(scope);
  }

  return parts.length > 0 ? parts.join(" · ") : undefined;
}
